import { REQUEST_CODES } from '~/constants/const-http';
import {computed, reactive, useRoute, useRouter} from "@nuxtjs/composition-api";
import { useAsyncState, toValue } from "@vueuse/core";

import { getProductFilter } from "~/services/vacation";
import { useRouteQuery } from '@/composables/router';
import { omit } from '~/utils/helpers';
import type {MaybeRefOrGetter} from "@vueuse/shared";
import type {GetVacationFilterParam} from "~/services/model";
import {consola} from "consola";

type Item =
  | {
      value: string | number;
      label: string;
      id: string | number;
      name: string;
      type?: "item";
      // 字段名称用来在url 中记录
    }
  | {
      type: "title";
      label: string;
      id: string | number;
      value: string | number;
    };

export function useProductVacationFilter(param: MaybeRefOrGetter<GetVacationFilterParam>) {
  const searchData = reactive({
    date_start: useRouteQuery<string>('date_start', ''),
    date_end: useRouteQuery<string>('date_end', ''),
    destination: useRouteQuery<string>('destination', ''),
    day_range: useRouteQuery<string>('day_range', ''),
    month: useRouteQuery<string>('month', ''),
    sort_name: useRouteQuery('sort_name', '1'),
    sort_type: useRouteQuery('sort_type', ''),
    tag: useRouteQuery('tag', ''),
    keyword: useRouteQuery<string>('q', ''),
    product_type: useRouteQuery<string>('product_type', ''),
  })
  const { state, execute, isLoading } = useAsyncState(
    async () => {
      const {code, data, msg} = await getProductFilter(toValue(param) );
      if(code !== REQUEST_CODES.ERROR_OK){
        throw new Error(msg);
      }
      return data;
    },
    {
      destination: [],
      travel: [],
      days: [],
      tag: [],
      product_type: [],
    },
    {
      immediate: false,
      onSuccess: res => consola.success('useProductVacationFilter', res)
    }
  );

  const cities = computed(() =>
    state.value.destination.map((city) => ({
      label: city.name,
      value: city.id,
      name: "destination",
      id: city.id,
    }))
  );

  const travelDates = computed(() => {
    return state.value.travel.reduce<Array<Item>>((result, item, index) => {
      result.push({
        label: item.year,
        type: "title",
        id: `y-${index}`,
        value: item.year,
      });
      item.month.forEach((m) => {
        result.push({
          type: "item",
          name: "month",
          value: m.id,
          id: m.id,
          label: `${m.name}月`,
        });
      });
      return result;
    }, []);
  });

  const days = computed(() =>
      state.value.days.map((item) => ({
        label: item.value,
        id: [item.gte, item.lte].join("-"),
        name: "day_range",
        value: [item.gte, item.lte].join("-"),
      }))
    );
  const tags = computed(() => state.value.tag.map(item => ({
    name: 'tag',
    id: item.id,
    label: item.name,
    value: item.id
  })))
  const types = computed(() =>
    state.value.product_type.map((city) => ({
      label: city.name,
      value: city.id,
      name: "product_type",
      id: city.id,
    }))
  );

  // const dateStart = useRouteQuery('date_start', '');
  // const dateEnd = useRouteQuery('date_end', '')
  const dateRange = computed(() => [
    searchData.date_start,
    searchData.date_end,
  ]);
  const router = useRouter();
  const route = useRoute();
  const onDateRangeChange = (value: null | [string, string]) => {
    if(!value){
      value = ['', '']
    }
    const [date_start, date_end] = value;
    window.location.href = router.resolve({
      query: {
        ...route.value.query,
        date_start,
        date_end
      }
    }).href
  }


  const labels = computed(() => {
    const createLabel = (label: string, props: string[]) => ({label, props});
    const {
      keyword,
      destination,
      month,
      date_start,
      date_end,
      day_range,
      tag,
      product_type
    } = searchData;
    const labels = [];
    // 关键词
    if(keyword)labels.push(createLabel(keyword, ['q']));

    // 目的地
    const checkedDestination = cities.value.find(item => item.value === destination);
    if(checkedDestination) labels.push(createLabel(checkedDestination.label, ['destination']));

    // 线路分类
    const checkedType = types.value.find(item => item.value == product_type);
    if(checkedType) labels.push(createLabel(checkedType.label, ['product_type']));

    // 出发日期
    const checkedMonth = travelDates.value.find(item => item.value === month);
    if(checkedMonth) labels.push(createLabel(checkedMonth.label, ['month']));

    // 自定义日期  2023/12/1-2023/12/1
    if(date_start && date_end) labels.push(createLabel([date_start, date_end].map(v => v.replaceAll('-', '/')).join('-'), ['date_start', 'date_end']));

    // 行程天数
    const checkedDay = days.value.find(item => item.value === day_range);
    if(checkedDay) labels.push(createLabel(checkedDay.label, ['day_range']));

    const checkedTag = tags.value.find(item => item.value === tag)
    if(checkedTag) labels.push(createLabel(checkedTag.label, ['tag']));

    return labels.map(item=>({
      ...item,
      query: omit(route.value.query, [...item.props, 'page_index'])
    }));
  })
  return {
    state,
    isLoading,
    execute,

    cities,
    travelDates,
    days,
    tags,
    labels,
    types,
    dateRange,
    onDateRangeChange,

    searchData
  };
}
